/**
 * Explore Search Results - Animated search results display
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Link } from 'react-router-dom';
import { ExploreWeaviateSearchResult } from '../../Model/Explore/ExploreWeaviateSearchResult';
import { ExploreWeaviateSearchResultEntry } from '../../Model/Explore/ExploreWeaviateSearchResultEntry';
import { MagazineCard } from '../ExploreCards/MagazineCard';
import { ExploreEntry } from '../../Model/Explore/ExploreEntry';
import { Recipe } from '../../Model/Diet/Recipe/Recipe';
import { FitnessProgramTemplate } from '../../Model/Coach/Program/Template/FitnessProgramTemplate';
import { MindfulnessProgramTemplate } from '../../Model/Coach/Mindfulness/Template/MindfulnessProgramTemplate';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useRootStore } from '../../Store/useRootStore';
import { getAthleteAllTags } from '../../Model/Athlete/Athlete';
import { getMediaLargeOrMediumOrSmallestUriObject, getMediaMedium } from '../../Model/Media/Media';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ResultsSection = styled.div`
  margin-bottom: 40px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  animation: ${fadeInUp} 0.4s ease-out;
`;

const SectionIcon = styled.span`
  font-size: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
`;

const SectionCount = styled.span`
  font-size: 0.9rem;
  color: #888;
  background: #f6f6f7;
  padding: 4px 12px;
  border-radius: 12px;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ResultItem = styled.div<{ delay: number }>`
  animation: ${fadeInUp} 0.5s ease-out forwards;
  animation-delay: ${({ delay }) => delay}ms;
  opacity: 0;
`;

const ProgramCardLink = styled(Link)`
  text-decoration: none;
  display: block;
`;

const ProgramCard = styled.div`
  display: flex;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  height: 180px;

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);

    .image-container {
      transform: scale(1.05);
    }

    .arrow {
      transform: translateX(4px);
      opacity: 1;
    }

    .accent-line {
      width: 60px;
    }
  }

  @media (max-width: 600px) {
    height: 140px;
  }
`;

const ProgramImageContainer = styled.div`
  width: 40%;
  min-width: 140px;
  position: relative;
  overflow: hidden;
  isolation: isolate;
`;

const ProgramImage = styled.div<{ image?: string; fallbackColor: string }>`
  position: absolute;
  inset: -1px;
  background: ${({ image, fallbackColor }) =>
    image ? `url('${image}')` : fallbackColor};
  background-size: cover;
  background-position: center;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
`;

const ProgramAccentLine = styled.div`
  width: 40px;
  height: 3px;
  background: #00dda2;
  border-radius: 2px;
  margin-bottom: 12px;
  transition: width 0.3s ease;
`;

const ProgramArrow = styled.div`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.5;
  transition: all 0.3s ease;
  font-size: 20px;
  color: #00dda2;
`;

const ProgramContent = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
`;

const ProgramTypeLabel = styled.span<{ labelColor?: string }>`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: ${({ labelColor }) => labelColor || '#00dda2'};
  margin-bottom: 8px;
`;

const ProgramTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: #1a1a2e;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProgramMeta = styled.div`
  font-size: 0.85rem;
  color: #666;
`;

type SectionConfig = {
  key: keyof ExploreWeaviateSearchResult;
  icon: string;
  titleKey: string;
  defaultTitle: string;
};

const SECTIONS: SectionConfig[] = [
  { key: 'Content', icon: '📚', titleKey: 'exploreSearch.contentSection', defaultTitle: 'Content' },
  { key: 'Explore', icon: '🔍', titleKey: 'exploreSearch.exploreSection', defaultTitle: 'Explore' },
  { key: 'Recipe', icon: '🍳', titleKey: 'exploreSearch.recipeSection', defaultTitle: 'Recipes' },
  {
    key: 'FitnessProgram',
    icon: '💪',
    titleKey: 'exploreSearch.fitnessProgramSection',
    defaultTitle: 'Fitness Programs',
  },
  {
    key: 'MindfulnessProgram',
    icon: '🧘',
    titleKey: 'exploreSearch.mindfulnessProgramSection',
    defaultTitle: 'Mindfulness Programs',
  },
];

export type ExploreSearchResultsProps = {
  results?: ExploreWeaviateSearchResult;
  categoryEntries?: ExploreEntry[];
  linkPrefix?: string;
  onItemClick?: () => void;
};

export const ExploreSearchResults: React.FC<ExploreSearchResultsProps> = observer(
  ({ results, categoryEntries, linkPrefix = '/academy/', onItemClick }) => {
    const { t } = useTrans();
    const { authentication } = useRootStore();
    const athleteTags = authentication.athlete ? getAthleteAllTags(authentication.athlete) : [];

    const renderEntry = (entry: ExploreWeaviateSearchResultEntry, index: number) => {
      if (!entry.content) return null;

      if (entry.type === 'fitnessProgram' && entry.content) {
        const program = entry.content as FitnessProgramTemplate;
        const image = program.getImage(athleteTags);
        const imageUrl = image ? (getMediaLargeOrMediumOrSmallestUriObject(image).uri || getMediaMedium(image)) : undefined;
        return (
          <ResultItem key={program.id} delay={index * 80}>
            <ProgramCardLink to={`/move/fitness-programs/${program.id}`} onClick={onItemClick}>
              <ProgramCard>
                <ProgramImageContainer>
                  <ProgramImage className="image-container" image={imageUrl} fallbackColor="linear-gradient(135deg, #00dda2 0%, #007763 100%)" />
                </ProgramImageContainer>
                <ProgramContent>
                  <ProgramAccentLine className="accent-line" />
                  <ProgramTypeLabel labelColor="#00dda2">
                    {t('exploreSearch.fitnessProgramBadge', { defaultValue: 'Fitness Program' })}
                  </ProgramTypeLabel>
                  <ProgramTitle>{program.defaultName || program.templateName}</ProgramTitle>
                  <ProgramMeta>
                    {program.microCyclesCount} {t('common.weeks', { defaultValue: 'weeks' })} • {program.minDaysPerWeek}
                    -{program.maxDaysPerWeek} {t('common.daysPerWeek', { defaultValue: 'days/week' })}
                  </ProgramMeta>
                  <ProgramArrow className="arrow">→</ProgramArrow>
                </ProgramContent>
              </ProgramCard>
            </ProgramCardLink>
          </ResultItem>
        );
      }

      if (entry.type === 'mindfulnessProgram' && entry.content) {
        const program = entry.content as MindfulnessProgramTemplate;
        const image = program.getImage(athleteTags);
        const imageUrl = image ? (getMediaLargeOrMediumOrSmallestUriObject(image).uri || getMediaMedium(image)) : undefined;
        return (
          <ResultItem key={program.id} delay={index * 80}>
            <ProgramCardLink to={`/mental-health/mindfulness-programs/${program.id}`} onClick={onItemClick}>
              <ProgramCard>
                <ProgramImageContainer>
                  <ProgramImage className="image-container" image={imageUrl} fallbackColor="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" />
                </ProgramImageContainer>
                <ProgramContent>
                  <ProgramAccentLine className="accent-line" />
                  <ProgramTypeLabel labelColor="#667eea">
                    {t('exploreSearch.mindfulnessProgramBadge', { defaultValue: 'Mindfulness Program' })}
                  </ProgramTypeLabel>
                  <ProgramTitle>{program.defaultName || program.templateName}</ProgramTitle>
                  <ProgramMeta>
                    {program.weeksCount} {t('common.weeks', { defaultValue: 'weeks' })} • {program.minDaysPerWeek}-
                    {program.maxDaysPerWeek} {t('common.daysPerWeek', { defaultValue: 'days/week' })}
                  </ProgramMeta>
                  <ProgramArrow className="arrow">→</ProgramArrow>
                </ProgramContent>
              </ProgramCard>
            </ProgramCardLink>
          </ResultItem>
        );
      }

      if (entry.type === 'recipe' || (entry.content && 'contentType' in entry.content)) {
        return (
          <ResultItem key={entry.content!.id} delay={index * 80}>
            <div onClick={onItemClick}>
              <MagazineCard entry={entry.content as ExploreEntry} linkPrefix={linkPrefix} />
            </div>
          </ResultItem>
        );
      }

      return null;
    };

    const renderCategoryEntry = (entry: ExploreEntry, index: number) => {
      return (
        <ResultItem key={entry.id} delay={index * 80}>
          <div onClick={onItemClick}>
            <MagazineCard entry={entry} linkPrefix={linkPrefix} />
          </div>
        </ResultItem>
      );
    };

    // Render category entries (from ExploreEntryBuilder search)
    if (categoryEntries && categoryEntries.length > 0) {
      return (
        <ResultsSection>
          <ResultsGrid>{categoryEntries.map((entry, index) => renderCategoryEntry(entry, index))}</ResultsGrid>
        </ResultsSection>
      );
    }

    // Render Weaviate search results
    if (!results) return null;

    return (
      <>
        {SECTIONS.map((section) => {
          const items = results[section.key] as ExploreWeaviateSearchResultEntry[];
          if (!items || items.length === 0) return null;

          return (
            <ResultsSection key={section.key}>
              <SectionHeader>
                <SectionIcon>{section.icon}</SectionIcon>
                <SectionTitle>{t(section.titleKey, { defaultValue: section.defaultTitle })}</SectionTitle>
                <SectionCount>{items.length}</SectionCount>
              </SectionHeader>
              <ResultsGrid>{items.map((entry, index) => renderEntry(entry, index))}</ResultsGrid>
            </ResultsSection>
          );
        })}
      </>
    );
  },
);
